import { useEffect, useLayoutEffect, useRef, useState, ReactNode, MutableRefObject } from 'react';

/**
 * The Read list as a stack of screen-height pages.
 *
 * Rows are packed into pages that each fit the visible area, the container
 * snaps one page at a time (touch, trackpad, wheel, keyboard), and when you
 * move forward off a page you had in front of you for at least DWELL_MS the
 * parent is told which rows were on it, so it can mark them read.
 *
 * Flinging past pages you never looked at does not count: only the page you
 * were actually on fires onLeavePage. Going backward never fires it.
 *
 * After the last page of rows there is one more page (renderEnd), so the last
 * real page can be left behind like any other.
 */

export interface PagedRowsHandle {
  next: () => void;
  prev: () => void;
  goTo: (page: number) => void;
  page: () => number;
  pageCount: () => number; // pages of rows, not counting the end page
}

interface PagedRowsProps<T extends { id: string }> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  renderEnd: () => ReactNode;
  selectedIndex: number;
  onLeavePage: (indices: number[]) => void;
  controller?: MutableRefObject<PagedRowsHandle | null>;
}

const DWELL_MS = 600;       // a page has to be in front of you this long to count as seen
const SETTLE_MS = 120;      // quiet time after the last scroll event before we read the position
const WHEEL_STEP = 24;      // accumulated deltaY that commits a page turn
const WHEEL_LOCK_MS = 600;  // ignore further wheel input this long after a turn
const WHEEL_TAIL_MS = 150;  // and keep ignoring while events keep arriving this close together (trackpad inertia)
const PAGE_SLACK_PX = 32;   // room at the foot of each page for the counter and a selected row's extra line

// Greedy fill: rows go on the current page until the next one would not fit.
function pack(heights: number[], pageHeight: number): number[][] {
  const budget = Math.max(pageHeight - PAGE_SLACK_PX, 1);
  const pages: number[][] = [];
  let current: number[] = [];
  let used = 0;
  heights.forEach((h, i) => {
    if (current.length > 0 && used + h > budget) {
      pages.push(current);
      current = [];
      used = 0;
    }
    current.push(i);
    used += h;
  });
  if (current.length > 0) pages.push(current);
  return pages;
}

export default function PagedRows<T extends { id: string }>({
  items, renderItem, renderEnd, selectedIndex, onLeavePage, controller,
}: PagedRowsProps<T>) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const rowEls = useRef<(HTMLDivElement | null)[]>([]);
  const [pageHeight, setPageHeight] = useState(0);
  const [width, setWidth] = useState(0);
  // Pages are packed for one exact list + size. Until the current one is
  // packed, every row renders on a single tall page so it can be measured.
  const [packed, setPacked] = useState<{ key: string; pages: number[][] }>({ key: '', pages: [] });
  const [current, setCurrent] = useState(0);

  const currentRef = useRef(0);
  const headingToRef = useRef<number | null>(null); // page a smooth scroll is on its way to
  const enteredAtRef = useRef(performance.now());
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onLeavePageRef = useRef(onLeavePage);
  onLeavePageRef.current = onLeavePage;

  const itemsKey = items.map((item) => item.id).join('|');
  const layoutKey = `${itemsKey}#${pageHeight}#${width}`;
  const ready = pageHeight > 0 && packed.key === layoutKey;
  const pages: number[][] = ready
    ? packed.pages
    : items.length > 0 ? [items.map((_, i) => i)] : [];
  const pagesRef = useRef<number[][]>(pages);
  pagesRef.current = pages;

  const pageCount = pages.length; // pages of rows; the end page is index pageCount
  const lastPage = pageCount;

  // Follow the scroller's size (rotation, window resize, sidebar toggles)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      setPageHeight(el.clientHeight);
      setWidth(el.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // New list: back to the first page, clock restarts
  useLayoutEffect(() => {
    currentRef.current = 0;
    enteredAtRef.current = performance.now();
    setCurrent(0);
  }, [itemsKey]);

  // Measure the rows (rendered flat by the render above) and pack them.
  useLayoutEffect(() => {
    if (pageHeight === 0 || items.length === 0) return;
    rowEls.current.length = items.length;
    const heights = items.map((_, i) => rowEls.current[i]?.getBoundingClientRect().height ?? 0);
    setPacked({ key: layoutKey, pages: pack(heights, pageHeight) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey]);

  // After a repack, put the page we were on back under the viewport
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || !ready) return;
    el.scrollTop = Math.min(currentRef.current, lastPage) * pageHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packed, ready]);

  function arriveAt(next: number) {
    headingToRef.current = null;
    const prev = currentRef.current;
    if (next === prev) return;
    const now = performance.now();
    const dwelled = now - enteredAtRef.current >= DWELL_MS;
    currentRef.current = next;
    enteredAtRef.current = now;
    setCurrent(next);
    const leftRows = pagesRef.current[prev];
    if (next > prev && dwelled && leftRows) {
      onLeavePageRef.current(leftRows);
    }
  }

  function goTo(page: number) {
    const el = scrollerRef.current;
    if (!el || !ready) return;
    const target = Math.max(0, Math.min(page, lastPage));
    headingToRef.current = target;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ top: target * pageHeight, behavior: reduce ? 'auto' : 'smooth' });
  }

  if (controller) {
    controller.current = {
      // Repeated presses stack while a turn is still in flight
      next: () => goTo((headingToRef.current ?? currentRef.current) + 1),
      prev: () => goTo((headingToRef.current ?? currentRef.current) - 1),
      goTo,
      page: () => currentRef.current,
      pageCount: () => pageCount,
    };
  }

  // Read the settled position after touch / programmatic scrolls
  function handleScroll() {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      const el = scrollerRef.current;
      if (!el || !ready) return;
      const page = Math.max(0, Math.min(Math.round(el.scrollTop / pageHeight), lastPage));
      arriveAt(page);
    }, SETTLE_MS);
  }

  // Wheel and trackpad: one page per gesture, never a partial scroll
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let acc = 0;
    let lockUntil = 0;
    let lastAt = 0;
    function onWheel(e: WheelEvent) {
      if (e.ctrlKey) return; // pinch-zoom on a trackpad arrives as a wheel event
      if (!ready) return;
      e.preventDefault();
      const now = performance.now();
      if (now < lockUntil) {
        lockUntil = Math.max(lockUntil, now + WHEEL_TAIL_MS);
        return;
      }
      if (now - lastAt > 200) acc = 0;
      lastAt = now;
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? pageHeight : 1;
      acc += e.deltaY * unit;
      if (Math.abs(acc) < WHEEL_STEP) return;
      const dir = acc > 0 ? 1 : -1;
      acc = 0;
      lockUntil = now + WHEEL_LOCK_MS;
      goTo(currentRef.current + dir);
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, pageHeight, lastPage]);

  // Keyboard cursor moved to a row on another page: turn to it
  useEffect(() => {
    if (!ready || selectedIndex < 0) return;
    const page = packed.pages.findIndex((rows) => rows.includes(selectedIndex));
    if (page >= 0 && page !== currentRef.current) goTo(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, packed, ready]);

  useEffect(() => () => { if (settleTimer.current) clearTimeout(settleTimer.current); }, []);

  return (
    <div
      ref={scrollerRef}
      onScroll={handleScroll}
      className="flex-1 min-h-0 overflow-y-auto snap-y snap-mandatory overscroll-contain"
      data-paged-rows
      data-page={current}
      data-page-count={pageCount}
    >
      {pages.map((rows, pageIndex) => (
        <div
          key={pageIndex}
          className="relative overflow-hidden snap-start snap-always"
          style={ready ? { height: pageHeight } : undefined}
          data-page-index={pageIndex}
        >
          {rows.map((index) => (
            <div key={items[index].id} ref={(el) => { rowEls.current[index] = el; }}>
              {renderItem(items[index], index)}
            </div>
          ))}
          {ready && pageCount > 1 && (
            <div className="absolute bottom-2 right-4 kicker pointer-events-none" aria-hidden="true">
              {pageIndex + 1} / {pageCount}
            </div>
          )}
        </div>
      ))}
      {ready && (
        <div
          className="relative overflow-hidden snap-start snap-always"
          style={{ height: pageHeight }}
          data-page-index={lastPage}
        >
          {renderEnd()}
        </div>
      )}
    </div>
  );
}
